import React, {useState, useEffect, useRef} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";
import {formatDateTimeString, formatDateString, formatDateToAmericanFormat} from "../../utils/utils";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import ModalDiv from "../../ModalDiv";
import AfterSaleViewer from "./AfterSaleViewer";
import StatusSelect from "./StatusSelect";
import NewButton from '../common/NewButton';
import SearchButton from '../common/SearchButton';
import ListPageLayout from '../common/ListPageLayout';
import TableRowActions from '../common/TableRowActions';
import WhatsAppButton from '../../core/WhatsAppButton';
import {buildDefaultWhatsAppMessage} from '../../utils/whatsappUtils';
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const getComparableValue = (item, key) => {
    const value = item[key];
    if (key === 'afterSaleDate' && value) {
        return new Date(value + 'T00:00:00').getTime();
    }
    if (value === undefined || value === null) {
        return '';
    }
    return value;
};

const compareValues = (a, b, key, direction) => {
    const valueA = getComparableValue(a, key);
    const valueB = getComparableValue(b, key);

    if (valueA < valueB) {
        return direction === 'ascending' ? -1 : 1;
    }
    if (valueA > valueB) {
        return direction === 'ascending' ? 1 : -1;
    }
    return 0;
};

const AfterSaleList = () => {
    const {hasUserPermission, loadingPermissions} = usePermission();
    let [afterSales, setAfterSales] = useState([]);
    const [queryString, setQueryString] = useState('');
    const inputQueryString = useRef(null);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const [filterStatus, setFilterStatus] = useState('PENDING');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [viewingAfterSaleId, setViewingAfterSaleId] = useState(null);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('ALL');
    const [companyLabels, setCompanyLabels] = useState({});

    const statusLabels = {
        PENDING: 'Pendente',
        RESOLVED: 'Resolvido',
        CANCELED: 'Cancelado'
    };

    async function fetchAccessibleCompanies() {
        try {
            const response = await makeRequest(apiUrl + '/api/aftersales/accessible-companies', 'GET');
            if (Array.isArray(response)) {
                const labelMap = response.reduce((acc, company) => {
                    const label = company.fantasyName && company.fantasyName.trim() !== '' ? company.fantasyName : company.name;
                    acc[company.id] = label;
                    return acc;
                }, {});
                setCompanyLabels(labelMap);

                const formattedOptions = response.map(company => ({
                    value: company.id.toString(),
                    label: labelMap[company.id]
                }));

                if (formattedOptions.length > 1) {
                    setCompanyOptions([{value: 'ALL', label: 'Todos'}, ...formattedOptions]);
                } else {
                    setCompanyOptions([]);
                }
            } else {
                setCompanyLabels({});
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error('Error fetching accessible companies:', error);
            setCompanyLabels({});
            setCompanyOptions([]);
        }
    }

    async function makeQuery(query, startDateFilter, endDateFilter, statusFilter, companyFilterOverride) {
        try {
            setLoading(true);
            let url = apiUrl + '/api/aftersales';

            const params = [];
            const companyFilterValue = companyFilterOverride !== undefined ? companyFilterOverride : selectedCompanyId;

            if (query && query !== '') {
                url = apiUrl + '/api/aftersales/query=' + query;
            } else if (statusFilter && statusFilter !== 'ALL') {
                url = apiUrl + '/api/aftersales/status=' + statusFilter;
            }

            if (companyFilterValue && companyFilterValue !== 'ALL') {
                params.push('companyId=' + companyFilterValue);
            }

            if (startDateFilter) {
                params.push('startDate=' + formatDateToAmericanFormat(startDateFilter));
            }
            if (endDateFilter) {
                params.push('endDate=' + formatDateToAmericanFormat(endDateFilter));
            }

            if (params.length > 0) {
                url += (url.includes('?') ? '&' : '?') + params.join('&');
            }

            const response = await makeRequest(url, 'GET');

            // Filter by date range on client side if needed
            let filteredResults = response;
            if (startDateFilter || endDateFilter) {
                filteredResults = response.filter(afterSale => {
                    if (!afterSale.afterSaleDate) return true;

                    // Normalizar datas para comparar apenas dia/mês/ano (sem horas)
                    const afterSaleDate = new Date(afterSale.afterSaleDate + 'T00:00:00');
                    afterSaleDate.setHours(0, 0, 0, 0);

                    if (startDateFilter) {
                        const startDateNormalized = new Date(startDateFilter);
                        startDateNormalized.setHours(0, 0, 0, 0);
                        if (afterSaleDate < startDateNormalized) return false;
                    }

                    if (endDateFilter) {
                        const endDateNormalized = new Date(endDateFilter);
                        endDateNormalized.setHours(23, 59, 59, 999);
                        if (afterSaleDate > endDateNormalized) return false;
                    }

                    return true;
                });
            }

            filteredResults.sort((a, b) => compareValues(a, b, 'afterSaleDate', 'ascending'));
            setAfterSales(filteredResults);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleSearch = () => {
        setSortConfig({key: 'afterSaleDate', direction: 'ascending'});
        makeQuery(queryString, startDate, endDate, filterStatus);
    };

    async function loadByStatus(status) {
        try {
            setLoading(true);
            setSortConfig({key: 'afterSaleDate', direction: 'ascending'});
            const url = status === 'ALL'
                ? apiUrl + '/api/aftersales'
                : apiUrl + '/api/aftersales/status=' + status;
            const response = await makeRequest(url, 'GET');
            const sortedResponse = Array.isArray(response)
                ? [...response].sort((a, b) => compareValues(a, b, 'afterSaleDate', 'ascending'))
                : [];
            setAfterSales(sortedResponse);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleCompanyFilterChange = (e) => {
        const value = e.target.value;
        setSelectedCompanyId(value);
        setSortConfig({key: 'afterSaleDate', direction: 'ascending'});
        makeQuery(queryString, startDate, endDate, filterStatus, value);
    };

    useEffect(() => {
        if (inputQueryString.current) {
            inputQueryString.current.focus();
        }
        const initialize = async () => {
            await fetchAccessibleCompanies();
            await makeQuery('', startDate, endDate, 'PENDING');
        };
        initialize();
    }, []);

    async function deleteAfterSale(id) {
        if (!await showConfirm('Tem certeza que deseja excluir este registro?')) {
            return;
        }
        try {
            setLoading(true);
            const url = apiUrl + '/api/aftersales/id=' + id;
            await makeRequest(url, 'DELETE');
            await handleSearch();
        } catch (error) {
            console.error('Error deleting:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleInputChange = (e) => {
        const string = e.target.value;
        setQueryString(string);
    };

    const handleStatusFilterChange = (e) => {
        const status = e.target.value;
        setFilterStatus(status);
        setQueryString('');
    };

    const handleDateChange = async (afterSale, newDate) => {
        if (!newDate) return;

        // Se a data for igual à atual, não faz nada
        const currentDate = afterSale.afterSaleDate ? new Date(afterSale.afterSaleDate + 'T00:00:00') : null;
        if (currentDate && newDate.getTime() === currentDate.getTime()) {
            return;
        }

        const confirmed = await showConfirm(
            `Confirma a alteração da data do pós-venda ${afterSale.code} para ${newDate.toLocaleDateString('pt-BR')}?`
        );

        if (confirmed) {
            try {
                setLoading(true);
                const url = apiUrl + '/api/aftersales/' + afterSale.id + '/reschedule';
                const formattedDate = formatDateToAmericanFormat(newDate);
                await makeRequest(url, 'PUT', {
                    'Content-Type': 'application/json',
                }, JSON.stringify({afterSaleDate: formattedDate}));
                await handleSearch();
            } catch (error) {
                console.error('Error updating date:', error);
                showAlert('Erro ao atualizar a data.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleStatusChangeInList = async (afterSale, newStatus) => {
        if (newStatus === afterSale.status) return;

        const confirmed = await showConfirm(
            `Confirma a alteração do status do pós-venda ${afterSale.code} para "${statusLabels[newStatus]}"?`
        );

        if (confirmed) {
            try {
                setLoading(true);
                const updatedAfterSale = {...afterSale, status: newStatus};
                const url = apiUrl + '/api/aftersales';
                await makeRequest(url, 'PUT', {
                    'Content-Type': 'application/json',
                }, JSON.stringify(updatedAfterSale));
                await handleSearch();
            } catch (error) {
                console.error('Error updating status:', error);
                showAlert('Erro ao atualizar o status.');
            } finally {
                setLoading(false);
            }
        }
    };

    const [sortConfig, setSortConfig] = useState({key: 'afterSaleDate', direction: 'ascending'});

    const sortedAfterSales = [...afterSales].sort((a, b) =>
        compareValues(a, b, sortConfig.key, sortConfig.direction)
    );

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({key, direction});
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };

    const showCompanyColumn = Object.keys(companyLabels).length > 1;

    const buildAfterSaleRowActions = (afterSale) => {
        const actions = [
            {
                key: 'whatsapp',
                label: 'WhatsApp',
                visible: !!afterSale.customerPhone,
                phoneNumber: afterSale.customerPhone,
                personName: afterSale.customerName,
                message: buildDefaultWhatsAppMessage(afterSale.customerName),
                render: () => (
                    <WhatsAppButton
                        phoneNumber={afterSale.customerPhone}
                        personName={afterSale.customerName}
                    />
                ),
            },
            {
                key: 'view',
                label: 'Visualizar',
                onClick: () => setViewingAfterSaleId(afterSale.id),
            },
        ];

        if (hasUserPermission(UserPermission.EDIT_AFTER_SALE)) {
            actions.push({
                key: 'edit',
                label: 'Editar',
                type: 'edit',
                onClick: () => navigate('/app/aftersalemanager?id=' + afterSale.id),
            });
        }

        if (hasUserPermission(UserPermission.REMOVE_AFTER_SALE)) {
            actions.push({
                key: 'delete',
                label: 'Excluir',
                type: 'delete',
                onClick: () => deleteAfterSale(afterSale.id),
                buttonProps: {className: 'remove-button'},
            });
        }

        return actions;
    };

    if (loadingPermissions) {
        return <Loading/>;
    }

    if (hasUserPermission(UserPermission.LIST_AFTER_SALE) === false) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <>
            {viewingAfterSaleId && (
                <ModalDiv closeModal={() => setViewingAfterSaleId(null)}>
                    <AfterSaleViewer
                        afterSaleId={viewingAfterSaleId}
                        onClose={() => setViewingAfterSaleId(null)}
                        onStatusChange={() => handleSearch()}
                    />
                </ModalDiv>
            )}

            <ListPageLayout
                tableWidth={900}
                header={
                    <>
            <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                <div className="header-label">Pós-Vendas</div>
                {hasUserPermission(UserPermission.CREATE_AFTER_SALE) && (
                    <NewButton className={"new-button"} onClick={() => navigate('/app/aftersalemanager')}/>
                )}
            </div>
            {companyOptions.length > 0 && (
                <div>
                    <div>Empresa</div>
                    <select value={selectedCompanyId} onChange={handleCompanyFilterChange}>
                        {companyOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
                    </>
                }
                filters={
                    <>
            <div style={{display: 'flex', marginBottom: '10px', gap: '10px', alignItems: 'flex-end'}}>

                <div style={{flex: 1}}>
                    <div>Pesquisar</div>
                    <input
                        ref={inputQueryString}
                        type="text"
                        placeholder="Código, cliente, telefone ou produto..."
                        value={queryString}
                        onChange={handleInputChange}
                        style={{width: '100%'}}
                    />
                </div>
                <div className="list-page-filters-field" style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                    <div>Pós-Venda</div>
                    <div className="list-page-filters-period">
                        <DatePicker
                            className="datepicker"
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            autoComplete="off"
                        />

                        <span className="list-page-filters-period-separator">até</span>

                        <DatePicker
                            className="datepicker"
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            autoComplete="off"
                        />
                    </div>
                </div>
                <div>
                    <div>Status</div>
                    <select value={filterStatus} onChange={handleStatusFilterChange}>
                        <option value="ALL">Todos</option>
                        <option value="PENDING">Pendente</option>
                        <option value="RESOLVED">Resolvido</option>
                        <option value="CANCELED">Cancelado</option>
                    </select>
                </div>
                <SearchButton onClick={handleSearch}/>
            </div>

                    </>
                }
            >
            <div className="list-page-aftersales">
            <table>
                <thead>
                <tr>
                    <th className="aftersale-col--optional" onClick={() => handleSort('code')} style={{cursor: 'pointer'}}>
                        Código {getSortIcon('code')}
                    </th>
                    {showCompanyColumn && (
                        <th className="aftersale-col--optional">Empresa</th>
                    )}
                    <th className="aftersale-col--essential aftersale-col--customer" onClick={() => handleSort('customerName')} style={{cursor: 'pointer'}}>
                        Cliente {getSortIcon('customerName')}
                    </th>
                    <th className="aftersale-col--optional" onClick={() => handleSort('customerPhone')} style={{cursor: 'pointer'}}>
                        Telefone {getSortIcon('customerPhone')}
                    </th>
                    <th className="aftersale-col--optional">Atendente</th>
                    <th className="aftersale-col--optional" onClick={() => handleSort('productName')} style={{cursor: 'pointer'}}>
                        Produto {getSortIcon('productName')}
                    </th>
                    <th className="aftersale-col--essential aftersale-col--status" onClick={() => handleSort('status')} style={{cursor: 'pointer'}}>
                        Status {getSortIcon('status')}
                    </th>
                    <th className="aftersale-col--essential aftersale-col--date" onClick={() => handleSort('afterSaleDate')} style={{cursor: 'pointer'}}>
                        Data Pós-Venda {getSortIcon('afterSaleDate')}
                    </th>
                    <th className="aftersale-col--actions"/>
                </tr>
                </thead>
                <tbody>
                {sortedAfterSales.map((afterSale) => (
                    <tr key={afterSale.id}>
                        <td className="aftersale-col--optional">{afterSale.code}</td>
                        {showCompanyColumn && (
                            <td className="aftersale-col--optional">{companyLabels[afterSale.companyId] || '-'}</td>
                        )}
                        <td className="aftersale-col--essential aftersale-col--customer">{afterSale.customerName}</td>
                        <td className="aftersale-col--optional">{afterSale.customerPhone}</td>
                        <td className="aftersale-col--optional">{afterSale.attendant || '-'}</td>
                        <td className="aftersale-col--optional">{afterSale.productName}</td>
                        <td className="aftersale-col--essential aftersale-col--status">
                             <StatusSelect
                                 value={afterSale.status}
                                 onChange={(newStatus) => handleStatusChangeInList(afterSale, newStatus)}
                             />
                        </td>
                        <td className="aftersale-col--essential aftersale-col--date">
                            <DatePicker
                                className="datepicker"
                                selected={afterSale.afterSaleDate ? new Date(afterSale.afterSaleDate + 'T00:00:00') : null}
                                onChange={(date) => handleDateChange(afterSale, date)}
                                dateFormat="dd/MM/yyyy"
                                autoComplete="off"
                                isClearable
                                popperModifiers={[
                                    {
                                        name: 'preventOverflow',
                                        options: {
                                            boundary: 'viewport'
                                        }
                                    }
                                ]}
                             popperPlacement="bottom-start"
                         />
                        </td>
                        <td className="aftersale-col--actions table-actions">
                            <TableRowActions actions={buildAfterSaleRowActions(afterSale)}/>
                        </td>
                    </tr>
                ))}
                </tbody>
                {sortedAfterSales.length === 0 && (
                    <tfoot>
                    <tr>
                        <td colSpan={showCompanyColumn ? 9 : 8}>
                            {loading ? 'Carregando...' : 'Nenhum registro encontrado'}
                        </td>
                    </tr>
                    </tfoot>
                )}
            </table>
            </div>
            </ListPageLayout>
        </>
    );
};

export default AfterSaleList;
